import React, { useEffect, useState, useRef } from 'react';
import { useApp } from '../../context/AppContext';
import { Skeleton } from '../../components/ui/skeleton';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Edit2, Plus, Users, Maximize2, DollarSign, Image, Save, X, Upload, Loader2, Trash2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const S = {
  page: { fontFamily: 'Manrope, sans-serif' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' },
  h1: { fontFamily: 'Cormorant Garamond, serif', fontSize: '28px', fontWeight: 600, color: '#1F3A2E' },
  subtitle: { fontSize: '13px', color: '#6E7F5B', marginTop: '2px' },
  addBtn: { display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#1F3A2E', color: 'white', fontSize: '13px', fontWeight: 700, padding: '10px 18px', borderRadius: '10px', border: 'none', cursor: 'pointer', fontFamily: 'Manrope, sans-serif' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '16px' },
  card: { background: 'white', borderRadius: '14px', border: '1px solid #EFE7DA', overflow: 'hidden', display: 'flex' },
  cardImg: { width: '100px', height: '100px', objectFit: 'cover', flexShrink: 0 },
  cardBody: { padding: '14px 16px', flex: 1, minWidth: 0 },
  cardName: { fontFamily: 'Cormorant Garamond, serif', fontSize: '17px', fontWeight: 600, color: '#1F3A2E', marginBottom: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  cardMeta: { display: 'flex', alignItems: 'center', gap: '12px', fontSize: '12px', color: '#6E7F5B', marginBottom: '8px' },
  chip: { fontSize: '11px', background: '#EFE7DA', color: '#3E5A3F', padding: '2px 8px', borderRadius: '20px', fontWeight: 500 },
  editBtn: { background: 'transparent', border: '1px solid #D8D1C6', borderRadius: '8px', padding: '6px 10px', cursor: 'pointer', color: '#6E7F5B', display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontFamily: 'Manrope, sans-serif' },
  label: { fontSize: '11px', fontWeight: 700, color: '#6E7F5B', textTransform: 'uppercase', letterSpacing: '0.07em', display: 'block', marginBottom: '5px' },
  saveBtn: { flex: 1, background: '#1F3A2E', color: 'white', fontSize: '13px', fontWeight: 700, padding: '12px', borderRadius: '10px', border: 'none', cursor: 'pointer', fontFamily: 'Manrope, sans-serif' },
  cancelBtn: { padding: '12px 16px', background: 'transparent', border: '1px solid #D8D1C6', borderRadius: '10px', fontSize: '13px', cursor: 'pointer', fontFamily: 'Manrope, sans-serif' },
};

export default function AdminRooms() {
  const { apiHeaders, language, formatPrice, t, BACKEND_URL, getImageUrl } = useApp();
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editRoom, setEditRoom] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingIdx, setUploadingIdx] = useState(null);

  const DEFAULT_MULT = { '1': 0.85, '2': 1.0, '3': 1.4, '4': 1.7, '5': 2.0, '6': 2.3 };
  const DEFAULT_CHILD_POLICY = { max_children: 2, brackets: [
    { min_age: 0, max_age: 6, price_type: 'percent', first_child_price: 0, additional_child_price: 50 },
    { min_age: 7, max_age: 11, price_type: 'percent', first_child_price: 50, additional_child_price: 50 },
  ] };
  const emptyRoom = { name: '', description_tr: '', description_en: '', capacity: 2, base_price: 2000, size_m2: 30, total_units: 1, images: ['', '', ''], amenities_tr: [], amenities_en: [], is_active: true, sort_order: 10, guest_multipliers: { ...DEFAULT_MULT }, cancellation_policy_tr: '', cancellation_policy_en: '', child_policy: JSON.parse(JSON.stringify(DEFAULT_CHILD_POLICY)) };
  const [form, setForm] = useState(emptyRoom);
  // Raw string inputs for amenities so the user can type commas/punctuation freely.
  // We only split into array on save.
  const [amenitiesTrText, setAmenitiesTrText] = useState('');
  const [amenitiesEnText, setAmenitiesEnText] = useState('');

  useEffect(() => { fetchRooms(); }, []);

  const fetchRooms = async () => {
    try { const res = await axios.get(`${BACKEND_URL}/api/rooms`); setRooms(res.data); } catch { /* silent */ }
    setLoading(false);
  };

  const openEdit = (room) => {
    setEditRoom(room);
    setForm({ ...room, images: room.images?.length >= 3 ? room.images : [...(room.images || []), '', '', ''].slice(0, 3), amenities_tr: room.amenities_tr || [], amenities_en: room.amenities_en || [], guest_multipliers: room.guest_multipliers || { ...DEFAULT_MULT }, child_policy: room.child_policy || JSON.parse(JSON.stringify(DEFAULT_CHILD_POLICY)) });
    setAmenitiesTrText((room.amenities_tr || []).join(', '));
    setAmenitiesEnText((room.amenities_en || []).join(', '));
    setDialogOpen(true);
  };

  const openAdd = () => {
    setEditRoom(null);
    setForm(emptyRoom);
    setAmenitiesTrText('');
    setAmenitiesEnText('');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const parseAmenities = (txt) => txt.split(',').map(a => a.trim()).filter(Boolean);
      const payload = {
        ...form,
        images: form.images.filter(i => i.trim()),
        capacity: parseInt(form.capacity),
        base_price: parseFloat(form.base_price),
        size_m2: parseInt(form.size_m2),
        total_units: Math.max(parseInt(form.total_units) || 1, 1),
        sort_order: parseInt(form.sort_order || 10),
        guest_multipliers: form.guest_multipliers || { ...DEFAULT_MULT },
        child_policy: {
          max_children: Math.max(0, parseInt(form.child_policy?.max_children ?? 2) || 0),
          brackets: (form.child_policy?.brackets || []).map(b => ({
            min_age: parseInt(b.min_age) || 0,
            max_age: parseInt(b.max_age) || 0,
            price_type: b.price_type === 'percent' ? 'percent' : 'fixed',
            first_child_price: parseFloat(b.first_child_price) || 0,
            additional_child_price: parseFloat(b.additional_child_price) || 0,
          })),
        },
        amenities_tr: parseAmenities(amenitiesTrText),
        amenities_en: parseAmenities(amenitiesEnText),
      };
      if (editRoom) { await axios.put(`${BACKEND_URL}/api/rooms/${editRoom._id}`, payload, apiHeaders()); toast.success(language === 'tr' ? 'Oda güncellendi' : 'Room updated'); }
      else { await axios.post(`${BACKEND_URL}/api/rooms`, payload, apiHeaders()); toast.success(language === 'tr' ? 'Oda eklendi' : 'Room added'); }
      setDialogOpen(false); fetchRooms();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    setSaving(false);
  };

  const handleDelete = async (room) => {
    const msg = language === 'tr'
      ? `"${room.name}" odasını silmek istediğinizden emin misiniz?\n\nBu işlem odayı pasif yapar; mevcut rezervasyonlar etkilenmez.`
      : `Are you sure you want to delete "${room.name}"?\n\nThis will deactivate the room; existing reservations are unaffected.`;
    if (!window.confirm(msg)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/rooms/${room._id}`, apiHeaders());
      toast.success(language === 'tr' ? 'Oda silindi' : 'Room deleted');
      fetchRooms();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  const handleImageUpload = async (file, idx) => {
    if (!file) return;
    setUploadingIdx(idx);
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await axios.post(`${BACKEND_URL}/api/upload`, fd, { ...apiHeaders(), headers: { ...apiHeaders().headers, 'Content-Type': 'multipart/form-data' } });
      const imgs = [...form.images]; imgs[idx] = res.data.url;
      setForm(f => ({ ...f, images: imgs }));
      toast.success(language === 'tr' ? 'Görsel yüklendi!' : 'Image uploaded!');
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload error'); }
    setUploadingIdx(null);
  };

  const amenities = (room) => (language === 'tr' ? room.amenities_tr : room.amenities_en) || [];

  return (
    <div style={S.page}>
      <div style={S.header}>
        <div>
          <h1 style={S.h1}>{language === 'tr' ? 'Oda Yönetimi' : 'Room Management'}</h1>
          <p style={S.subtitle}>{rooms.length} {language === 'tr' ? 'oda tipi' : 'room types'}</p>
        </div>
        <button style={S.addBtn} onClick={openAdd}><Plus size={15} /> {language === 'tr' ? 'Oda Ekle' : 'Add Room'}</button>
      </div>

      {loading ? (
        <div style={S.grid}>{[1,2,3,4,5].map(i => <Skeleton key={i} style={{ height: '100px', borderRadius: '14px' }} />)}</div>
      ) : (
        <div style={S.grid}>
          {rooms.map(room => (
            <div key={room._id} style={S.card}>
              {room.images?.[0] && <img src={getImageUrl(room.images[0])} alt={room.name} style={S.cardImg} />}
              {!room.images?.[0] && <div style={{ ...S.cardImg, background: '#F6F1E7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Image size={24} style={{ color: '#D8D1C6' }} /></div>}
              <div style={S.cardBody}>
                <p style={S.cardName}>{room.name}</p>
                <div style={S.cardMeta}>
                  <span><Users size={11} style={{ display: 'inline', verticalAlign: 'middle' }} /> {room.capacity}</span>
                  <span><Maximize2 size={11} style={{ display: 'inline', verticalAlign: 'middle' }} /> {room.size_m2}m²</span>
                  <span><DollarSign size={11} style={{ display: 'inline', verticalAlign: 'middle' }} /> {formatPrice(room.base_price)}</span>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '10px' }}>
                  {amenities(room).slice(0, 3).map((a, i) => <span key={i} style={S.chip}>{a}</span>)}
                  {amenities(room).length > 3 && <span style={S.chip}>+{amenities(room).length - 3}</span>}
                </div>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <button style={S.editBtn} onClick={() => openEdit(room)} data-testid={`edit-room-${room._id}`}><Edit2 size={12} /> {language === 'tr' ? 'Düzenle' : 'Edit'}</button>
                  <button
                    style={{ ...S.editBtn, color: '#dc2626', borderColor: '#fca5a5' }}
                    onClick={() => handleDelete(room)}
                    data-testid={`delete-room-${room._id}`}
                  >
                    <Trash2 size={12} /> {language === 'tr' ? 'Sil' : 'Delete'}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {dialogOpen && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) setDialogOpen(false); }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '1rem', overflowY: 'auto' }}
        >
          <div style={{ background: 'white', borderRadius: '20px', width: '100%', maxWidth: '640px', padding: '24px', margin: '20px auto', fontFamily: 'Manrope, sans-serif' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
              <h2 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '22px', fontWeight: 600, color: '#1F3A2E' }}>
                {editRoom ? (language === 'tr' ? 'Oda Düzenle' : 'Edit Room') : (language === 'tr' ? 'Yeni Oda' : 'New Room')}
              </h2>
              <button onClick={() => setDialogOpen(false)} style={{ background: '#F6F1E7', border: 'none', width: '32px', height: '32px', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <X size={16} style={{ color: '#6E7F5B' }} />
              </button>
            </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', paddingTop: '8px' }}>
            {/* Name + Specs row */}
            <div>
              <label style={S.label}>{language === 'tr' ? 'Oda Adı' : 'Room Name'}</label>
              <Input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} style={{ border: '1px solid #D8D1C6', borderRadius: '8px', fontSize: '14px' }} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr', gap: '10px' }}>
              {[
                { key: 'capacity', label: language === 'tr' ? 'Kapasite' : 'Capacity', type: 'number' },
                { key: 'base_price', label: language === 'tr' ? 'Fiyat (TRY/gece)' : 'Price (TRY/night)', type: 'number' },
                { key: 'size_m2', label: 'Alan (m²)', type: 'number' },
                { key: 'total_units', label: language === 'tr' ? 'Kontenjan' : 'Units', type: 'number' },
                { key: 'sort_order', label: language === 'tr' ? 'Sıra' : 'Order', type: 'number' },
              ].map(field => (
                <div key={field.key}>
                  <label style={S.label}>{field.label}</label>
                  <Input type={field.type} value={form[field.key]} onChange={e => setForm(f => ({...f, [field.key]: e.target.value}))} style={{ border: '1px solid #D8D1C6', borderRadius: '8px', fontSize: '14px' }} />
                </div>
              ))}
            </div>

            {/* Descriptions */}
            <div>
              <label style={S.label}>{language === 'tr' ? 'Açıklama (Türkçe)' : 'Description (Turkish)'}</label>
              <Textarea value={form.description_tr} onChange={e => setForm(f => ({...f, description_tr: e.target.value}))} style={{ border: '1px solid #D8D1C6', borderRadius: '8px', fontSize: '13px', minHeight: '70px' }} />
            </div>
            <div>
              <label style={S.label}>{language === 'tr' ? 'Açıklama (İngilizce)' : 'Description (English)'}</label>
              <Textarea value={form.description_en} onChange={e => setForm(f => ({...f, description_en: e.target.value}))} style={{ border: '1px solid #D8D1C6', borderRadius: '8px', fontSize: '13px', minHeight: '70px' }} />
            </div>

            {/* Images */}
            <div>
              <label style={S.label}><Image size={11} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '4px' }} />{language === 'tr' ? 'Fotoğraflar' : 'Photos'}</label>
              {form.images.map((img, idx) => (
                <div key={idx} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', marginBottom: '10px' }}>
                  <div style={{ width: '56px', height: '56px', borderRadius: '8px', overflow: 'hidden', flexShrink: 0, background: '#F6F1E7', border: '1px solid #D8D1C6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {img ? <img src={getImageUrl(img)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { e.target.style.display='none'; }} /> : <Image size={18} style={{ color: '#D8D1C6' }} />}
                  </div>
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '5px' }}>
                    <Input value={img} onChange={e => { const imgs = [...form.images]; imgs[idx] = e.target.value; setForm(f => ({...f, images: imgs})); }} placeholder={`URL ${idx + 1}`} style={{ border: '1px solid #D8D1C6', borderRadius: '8px', fontSize: '12px' }} />
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <label style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '11px', fontWeight: 600, color: '#1F3A2E', background: '#EFE7DA', border: '1px solid #D8D1C6', borderRadius: '6px', padding: '4px 10px', fontFamily: 'Manrope, sans-serif' }}>
                        {uploadingIdx === idx ? <><Loader2 size={11} style={{ animation: 'spin 1s linear infinite' }} /> {language === 'tr' ? 'Yükleniyor...' : 'Uploading...'}</> : <><Upload size={11} /> {language === 'tr' ? 'Yükle' : 'Upload'}</>}
                        <input type="file" accept="image/jpeg,image/jpg,image/png,image/webp" style={{ display: 'none' }} onChange={e => { if (e.target.files?.[0]) handleImageUpload(e.target.files[0], idx); }} disabled={uploadingIdx !== null} />
                      </label>
                      {img && (
                        <button type="button" onClick={() => { const imgs = [...form.images]; imgs[idx] = ''; setForm(f => ({...f, images: imgs})); }} style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '11px', color: '#dc2626', background: 'transparent', border: '1px solid #fca5a5', borderRadius: '6px', padding: '4px 8px', cursor: 'pointer' }}>
                          <Trash2 size={11} /> {language === 'tr' ? 'Kaldır' : 'Remove'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              <button type="button" onClick={() => setForm(f => ({...f, images: [...f.images, '']}))} style={{ fontSize: '12px', color: '#1F3A2E', background: 'transparent', border: '1px dashed #D8D1C6', borderRadius: '8px', padding: '7px 14px', cursor: 'pointer', width: '100%', fontFamily: 'Manrope, sans-serif' }}>
                + {language === 'tr' ? 'Fotoğraf Ekle' : 'Add Photo'}
              </button>
            </div>

            {/* Guest Multipliers */}
            <div style={{ background: '#F6F1E7', borderRadius: '10px', padding: '12px 14px' }}>
              <label style={S.label}>{language === 'tr' ? 'Kişi Sayısı Çarpanları (kapasite kadar)' : 'Guest Count Multipliers (up to capacity)'}</label>
              <p style={{ fontSize: '11px', color: '#6E7F5B', marginBottom: '10px', lineHeight: 1.4 }}>
                {language === 'tr'
                  ? `Örnek: 2 kişi çarpanı 1.0 iken taban fiyat (₺${form.base_price || 0}), 3 kişi çarpanı 1.4 ise fiyat ₺${((form.base_price || 0) * 1.4).toFixed(0)} olur.`
                  : `Example: 2-guest multiplier 1.0 = base price ₺${form.base_price || 0}, 3-guest multiplier 1.4 = ₺${((form.base_price || 0) * 1.4).toFixed(0)}.`}
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(parseInt(form.capacity) || 2, 6)}, 1fr)`, gap: '8px' }}>
                {Array.from({ length: Math.min(parseInt(form.capacity) || 2, 6) }, (_, i) => i + 1).map(n => (
                  <div key={n}>
                    <label style={{ ...S.label, fontSize: '10px' }}>{n} {language === 'tr' ? 'kişi' : 'guest'}</label>
                    <Input
                      type="number"
                      step="0.05"
                      min="0.1"
                      max="5"
                      value={(form.guest_multipliers && form.guest_multipliers[String(n)]) ?? DEFAULT_MULT[String(n)] ?? 1.0}
                      onChange={e => {
                        const val = parseFloat(e.target.value);
                        setForm(f => ({ ...f, guest_multipliers: { ...(f.guest_multipliers || {}), [String(n)]: isNaN(val) ? 1.0 : val } }));
                      }}
                      style={{ border: '1px solid #D8D1C6', borderRadius: '8px', fontSize: '13px', textAlign: 'center' }}
                      data-testid={`guest-multiplier-${n}`}
                    />
                    <p style={{ fontSize: '10px', color: '#6E7F5B', textAlign: 'center', marginTop: '2px' }}>
                      ₺{(((form.base_price || 0) * ((form.guest_multipliers && form.guest_multipliers[String(n)]) ?? DEFAULT_MULT[String(n)] ?? 1.0)) || 0).toFixed(0)}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Amenities */}
            <div>
              <label style={S.label}>{language === 'tr' ? 'Olanaklar TR (virgülle)' : 'Amenities TR (comma separated)'}</label>
              <Input value={amenitiesTrText} onChange={e => setAmenitiesTrText(e.target.value)} placeholder="WiFi, Jakuzi, Şömine, Klima..." style={{ border: '1px solid #D8D1C6', borderRadius: '8px', fontSize: '13px' }} data-testid="amenities-tr-input" />
            </div>
            <div>
              <label style={S.label}>{language === 'tr' ? 'Olanaklar EN (virgülle)' : 'Amenities EN (comma separated)'}</label>
              <Input value={amenitiesEnText} onChange={e => setAmenitiesEnText(e.target.value)} placeholder="WiFi, Jacuzzi, Fireplace, AC..." style={{ border: '1px solid #D8D1C6', borderRadius: '8px', fontSize: '13px' }} data-testid="amenities-en-input" />
            </div>

            {/* Cancellation Policy */}
            <div style={{ background: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: '10px', padding: '12px 14px' }}>
              <label style={{ ...S.label, color: '#9A3412' }}>{language === 'tr' ? 'İptal ve İade Koşulları (Türkçe)' : 'Cancellation & Refund Policy (Turkish)'}</label>
              <Textarea
                value={form.cancellation_policy_tr || ''}
                onChange={e => setForm(f => ({...f, cancellation_policy_tr: e.target.value}))}
                placeholder={language === 'tr' ? 'Örn: Giriş tarihine 7 gün kala yapılan iptallerde tam iade yapılır...' : 'e.g. Full refund for cancellations 7 days before check-in...'}
                style={{ border: '1px solid #FED7AA', borderRadius: '8px', fontSize: '13px', minHeight: '100px', background: 'white' }}
                data-testid="cancellation-policy-tr-textarea"
              />
              <label style={{ ...S.label, color: '#9A3412', marginTop: '10px' }}>{language === 'tr' ? 'İptal ve İade Koşulları (İngilizce)' : 'Cancellation & Refund Policy (English)'}</label>
              <Textarea
                value={form.cancellation_policy_en || ''}
                onChange={e => setForm(f => ({...f, cancellation_policy_en: e.target.value}))}
                placeholder="e.g. Full refund for cancellations 7 days before check-in..."
                style={{ border: '1px solid #FED7AA', borderRadius: '8px', fontSize: '13px', minHeight: '100px', background: 'white' }}
                data-testid="cancellation-policy-en-textarea"
              />
              <p style={{ fontSize: '11px', color: '#9A3412', marginTop: '6px' }}>
                {language === 'tr'
                  ? 'Bu metin rezervasyon adımında misafire gösterilir; onay kutusu işaretlenmeden ödemeye geçilemez.'
                  : 'This text is shown to guests during booking; payment cannot proceed without accepting it.'}
              </p>
            </div>

            {/* Child Policy */}
            <div style={{ background: '#F0FAF5', border: '1px solid #BBE5CB', borderRadius: '10px', padding: '12px 14px' }} data-testid="child-policy-editor">
              <label style={{ ...S.label, color: '#166534' }}>{language === 'tr' ? 'Çocuk Politikası' : 'Child Policy'}</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                <span style={{ fontSize: '12px', color: '#166534', fontWeight: 600 }}>{language === 'tr' ? 'Maks. çocuk sayısı:' : 'Max children:'}</span>
                <Input
                  type="number" min="0" max="4"
                  value={form.child_policy?.max_children ?? 2}
                  onChange={e => setForm(f => ({ ...f, child_policy: { ...(f.child_policy || {}), max_children: e.target.value } }))}
                  style={{ width: '70px', border: '1px solid #BBE5CB', borderRadius: '8px', fontSize: '13px', background: 'white' }}
                  data-testid="child-policy-max-children-input"
                />
              </div>
              {(form.child_policy?.brackets || []).map((b, i) => (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '62px 62px 1fr 1fr 1fr 24px', gap: '6px', alignItems: 'end', marginBottom: '8px' }}>
                  {[
                    { key: 'min_age', label: language === 'tr' ? 'Yaş (min)' : 'Age (min)' },
                    { key: 'max_age', label: language === 'tr' ? 'Yaş (max)' : 'Age (max)' },
                  ].map(field => (
                    <div key={field.key}>
                      <label style={{ fontSize: '10px', fontWeight: 700, color: '#166534', display: 'block', marginBottom: '3px' }}>{field.label}</label>
                      <Input
                        type="number" min="0"
                        value={b[field.key] ?? 0}
                        onChange={e => setForm(f => ({
                          ...f,
                          child_policy: {
                            ...(f.child_policy || {}),
                            brackets: (f.child_policy?.brackets || []).map((bb, j) => j === i ? { ...bb, [field.key]: e.target.value } : bb),
                          },
                        }))}
                        style={{ border: '1px solid #BBE5CB', borderRadius: '8px', fontSize: '13px', background: 'white' }}
                        data-testid={`child-bracket-${i}-${field.key}`}
                      />
                    </div>
                  ))}
                  <div>
                    <label style={{ fontSize: '10px', fontWeight: 700, color: '#166534', display: 'block', marginBottom: '3px' }}>{language === 'tr' ? 'Ücret Tipi' : 'Price Type'}</label>
                    <select
                      value={b.price_type === 'percent' ? 'percent' : 'fixed'}
                      onChange={e => setForm(f => ({
                        ...f,
                        child_policy: {
                          ...(f.child_policy || {}),
                          brackets: (f.child_policy?.brackets || []).map((bb, j) => j === i ? { ...bb, price_type: e.target.value } : bb),
                        },
                      }))}
                      style={{ width: '100%', height: '36px', padding: '0 8px', border: '1px solid #BBE5CB', borderRadius: '8px', fontSize: '12px', background: 'white', fontFamily: 'Manrope, sans-serif', cursor: 'pointer' }}
                      data-testid={`child-bracket-${i}-price-type`}
                    >
                      <option value="percent">{language === 'tr' ? '% (kişi başı fiyatın)' : '% (of per-person rate)'}</option>
                      <option value="fixed">{language === 'tr' ? '₺ (sabit/gece)' : '₺ (fixed/night)'}</option>
                    </select>
                  </div>
                  {[
                    { key: 'first_child_price', label: language === 'tr' ? '1. Çocuk' : '1st Child' },
                    { key: 'additional_child_price', label: language === 'tr' ? '2.+ Çocuk' : '2nd+ Child' },
                  ].map(field => (
                    <div key={field.key}>
                      <label style={{ fontSize: '10px', fontWeight: 700, color: '#166534', display: 'block', marginBottom: '3px' }}>
                        {field.label} ({b.price_type === 'percent' ? '%' : '₺'})
                      </label>
                      <Input
                        type="number" min="0"
                        value={b[field.key] ?? 0}
                        onChange={e => setForm(f => ({
                          ...f,
                          child_policy: {
                            ...(f.child_policy || {}),
                            brackets: (f.child_policy?.brackets || []).map((bb, j) => j === i ? { ...bb, [field.key]: e.target.value } : bb),
                          },
                        }))}
                        style={{ border: '1px solid #BBE5CB', borderRadius: '8px', fontSize: '13px', background: 'white' }}
                        data-testid={`child-bracket-${i}-${field.key}`}
                      />
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => setForm(f => ({ ...f, child_policy: { ...(f.child_policy || {}), brackets: (f.child_policy?.brackets || []).filter((_, j) => j !== i) } }))}
                    style={{ height: '36px', border: 'none', background: 'transparent', color: '#dc2626', cursor: 'pointer', fontSize: '16px', fontWeight: 700 }}
                    title={language === 'tr' ? 'Satırı sil' : 'Remove row'}
                    data-testid={`child-bracket-${i}-remove`}
                  >×</button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => setForm(f => ({ ...f, child_policy: { ...(f.child_policy || {}), brackets: [...(f.child_policy?.brackets || []), { min_age: 0, max_age: 11, price_type: 'percent', first_child_price: 0, additional_child_price: 50 }] } }))}
                style={{ fontSize: '12px', fontWeight: 700, color: '#166534', background: 'white', border: '1px dashed #86efac', borderRadius: '8px', padding: '7px 12px', cursor: 'pointer', fontFamily: 'Manrope, sans-serif' }}
                data-testid="child-bracket-add-button"
              >
                + {language === 'tr' ? 'Yaş Aralığı Ekle' : 'Add Age Bracket'}
              </button>
              <p style={{ fontSize: '11px', color: '#166534', marginTop: '8px' }}>
                {language === 'tr'
                  ? '% seçilirse fiyat, kişi başı gecelik fiyatın (çift kişilik oda fiyatı ÷ 2) yüzdesi olarak hesaplanır. 0 = ücretsiz. Çocuklar yaşa göre sıralanır; en küçük "1. Çocuk", diğerleri "2.+ Çocuk" tarifesinden eklenir. 12 yaş ve üzeri yetişkin sayılır.'
                  : 'With %, the fee is a percentage of the per-person nightly rate (double room price ÷ 2). 0 = free. Children are sorted by age; the youngest uses "1st Child", others "2nd+ Child". Age 12+ counts as adult.'}
              </p>
            </div>

            <div style={{ display: 'flex', gap: '10px', paddingTop: '4px' }}>
              <button style={S.saveBtn} onClick={handleSave} disabled={saving}>
                <Save size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '6px' }} />
                {saving ? (language === 'tr' ? 'Kaydediliyor...' : 'Saving...') : (language === 'tr' ? 'Kaydet' : 'Save')}
              </button>
              <button style={S.cancelBtn} onClick={() => setDialogOpen(false)}>
                <X size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '4px' }} />
                {language === 'tr' ? 'İptal' : 'Cancel'}
              </button>
            </div>
          </div>
        </div>
      </div>
      )}
    </div>
  );
}
